// Pure logic behind the role permissions matrix.
//
// Everything here takes the permission state as an argument and returns a plain
// value, so it can be unit-tested without mounting the matrix (samsar CLAUDE.md:
// test pure logic, do not render heavy components).
//
// `permissions` throughout is the matrix's state shape: { featureCode: Set(ops) }.

// The four operations every feature has. These read as a hierarchy -- view is
// implied by create, create by update -- so they sit together as one group.
export const CORE_OPERATIONS = ["view", "create", "update", "delete"];

// Capabilities: authority a specific feature declares, not a CRUD verb. They
// belong to no hierarchy and only appear where the feature supports them, which
// is why the matrix stops rendering them as columns -- a column implies every
// row could have answered, and for these most rows cannot.
export const CAPABILITY_OPERATIONS = ["force_resolve", "approve", "critical_update"];

export const OPERATION_LABELS = {
    view: "View",
    create: "Create",
    update: "Update",
    delete: "Delete",
    force_resolve: "Force Resolve",
    approve: "Approve",
    critical_update: "Critical Update",
};

// Operations the row's "Select all" never touches. critical_update changes how
// SOH, energy and related values are computed for a vehicle, so it must be
// granted by its own pill on purpose -- one click meant for the CRUD ops on
// Digital Twin must not hand it out. "Clear all" leaves it alone for the same
// reason: removing it is also an explicit act.
export const TOGGLE_ALL_EXCLUDED_OPERATIONS = ["critical_update"];

// The subset of a feature's supported operations that "Select all" acts on.
export const getToggleAllOps = (supportedOps) =>
    (supportedOps || []).filter((op) => !TOGGLE_ALL_EXCLUDED_OPERATIONS.includes(op));

// Whether the role holds every operation "Select all" would grant, i.e. whether
// the button should read "Clear all". Excluded operations are ignored, so a
// role holding all CRUD ops but not critical_update still reads "Clear all".
export const isToggleAllHeld = (currentOps, supportedOps) => {
    const ops = getToggleAllOps(supportedOps);
    return ops.length > 0 && ops.every((op) => !!currentOps?.has(op));
};

// Splits a feature's supported operations into the two groups the row renders.
// Order comes from the constants above, not from the backend's array, so two
// features never show the same chips in a different order.
export const splitOperations = (supportedOps) => {
    const supported = new Set(supportedOps || []);
    return {
        core: CORE_OPERATIONS.filter((op) => supported.has(op)),
        capabilities: CAPABILITY_OPERATIONS.filter((op) => supported.has(op)),
    };
};

// What the search box matches against: the display name AND the feature code.
// Admins recognise a feature by either, and the code is the half that is unique.
export const featureSearchText = (feature) =>
    `${feature?.featureName || ""} ${feature?.featureCode || ""}`.toLowerCase();

export const matchesSearch = (feature, term) => {
    const needle = (term || "").trim().toLowerCase();
    if (!needle) return true;
    return featureSearchText(feature).includes(needle);
};

// A search hit shows even inside a collapsed category. Hiding a match because of
// a collapse the admin set minutes ago reads as "no results" and is the classic
// way a filtered tree lies about what it holds.
export const isFeatureRowVisible = ({ matches, expanded, searching }) =>
    !!matches && (!!searching || !!expanded);

// Counts one feature. Grants are counted only against operations the feature
// actually supports: a stale grant for an op the backend no longer offers is
// not something this role can do, so it must not inflate the totals.
export const countFeature = (feature, supportedOps, permissions) => {
    const granted = permissions?.[feature?.featureCode];
    const held = (op) => !!granted?.has(op);
    const { core, capabilities } = splitOperations(supportedOps);
    const all = [...core, ...capabilities];
    return {
        permissionTotal: all.length,
        permissionGranted: all.filter(held).length,
        capabilityTotal: capabilities.length,
        capabilityGranted: capabilities.filter(held).length,
    };
};

const EMPTY_TOTALS = {
    featureTotal: 0,
    featureGranted: 0,
    permissionTotal: 0,
    permissionGranted: 0,
    capabilityTotal: 0,
    capabilityGranted: 0,
};

// Rolls a list of features up into the numbers a category badge and the panel
// summary both show. `getSupportedOps` is injected rather than imported so the
// caller keeps one source of truth for what a feature supports.
export const countFeatures = (features, permissions, getSupportedOps) =>
    (features || []).reduce((acc, feature) => {
        const counts = countFeature(feature, getSupportedOps(feature), permissions);
        // A feature counts as granted when the role holds ANY operation on it --
        // that is the question "does this role touch this feature at all".
        const touched = counts.permissionGranted > 0;
        return {
            featureTotal: acc.featureTotal + 1,
            featureGranted: acc.featureGranted + (touched ? 1 : 0),
            permissionTotal: acc.permissionTotal + counts.permissionTotal,
            permissionGranted: acc.permissionGranted + counts.permissionGranted,
            capabilityTotal: acc.capabilityTotal + counts.capabilityTotal,
            capabilityGranted: acc.capabilityGranted + counts.capabilityGranted,
        };
    }, EMPTY_TOTALS);

export const sumTotals = (totalsList) =>
    (totalsList || []).reduce(
        (acc, t) => ({
            featureTotal: acc.featureTotal + (t?.featureTotal || 0),
            featureGranted: acc.featureGranted + (t?.featureGranted || 0),
            permissionTotal: acc.permissionTotal + (t?.permissionTotal || 0),
            permissionGranted: acc.permissionGranted + (t?.permissionGranted || 0),
            capabilityTotal: acc.capabilityTotal + (t?.capabilityTotal || 0),
            capabilityGranted: acc.capabilityGranted + (t?.capabilityGranted || 0),
        }),
        EMPTY_TOTALS
    );

// "full" / "none" / "partial" drives the badge colour. Zero features is "none",
// not "full": an empty category has granted nothing, and painting it green would
// claim the opposite.
export const rollupState = ({ featureGranted, featureTotal }) => {
    if (!featureTotal) return "none";
    if (featureGranted === featureTotal) return "full";
    if (featureGranted === 0) return "none";
    return "partial";
};

// Next state for a row's "grant everything this feature supports" toggle.
// TOGGLE_ALL_EXCLUDED_OPERATIONS are neither granted nor revoked here, even when
// the caller passes them in supportedOps.
// Returns a new Set; never mutates the one passed in.
export const toggleAllForFeature = (currentOps, supportedOps) => {
    const next = new Set(currentOps || []);
    const ops = getToggleAllOps(supportedOps);
    const allHeld = isToggleAllHeld(next, supportedOps);
    ops.forEach((op) => (allHeld ? next.delete(op) : next.add(op)));
    return next;
};
